import { SimpleAndFilter } from "@impero-com/common/utils/indexing";

import { PageState } from "api/generated/model";
import { CONSTANTS } from "utils/constants";

import { PageFilterAndColumns, PageType } from "./types";

function isPageStateOfType<T extends PageType>(
    pageState: PageState,
    pageType: T
): pageState is Extract<PageState, { type: T }> {
    if (pageState.type === pageType) {
        return true;
    } else {
        return false;
    }
}

// Example:
//    unwrapPageStateAs(pageState, "ControlsQuarterly")  // value of type ControlsQuarterlyPageState
//    unwrapPageStateAs(pageState, "ControlsAll")        // value of type ControlsAllPageState
export function unwrapPageStateAs<T extends PageType>(
    pageState: PageState,
    pageType: T
): Extract<PageState, { type: T }> {
    if (isPageStateOfType(pageState, pageType)) {
        return pageState;
    } else {
        throw new Error(
            `unwrapPageStateAs(): expected ${pageType}, got: ${pageState.type}`
        );
    }
}

interface FilterAndColumnsWithCallbacks {
    filter: SimpleAndFilter | null;
    onFilterChange: ((newFilter: SimpleAndFilter | null) => void) | undefined;
    visibleColumnIds: Set<string> | undefined;
    onVisibleColumnsChange:
        | ((visibleColumnIds: Set<string>) => void)
        | undefined;
}

export function splitPageStateWithCallback<ST extends PageFilterAndColumns>(
    pageState: ST | undefined,
    onPageStateChange: ((newPageState: ST) => void) | undefined
): FilterAndColumnsWithCallbacks {
    if (pageState === undefined || onPageStateChange === undefined) {
        return {
            filter: null,
            visibleColumnIds: undefined,
            onFilterChange: undefined,
            onVisibleColumnsChange: undefined,
        };
    }

    const visibleColumnIds = pageState.visibleColumnIds;
    let filter = pageState.filter;
    if (filter && filter.filters.length === 0) {
        filter = null;
    }
    const onFilterChange = (newFilter: SimpleAndFilter | null): void => {
        onPageStateChange({
            ...pageState,
            filter: newFilter,
        });
    };
    const onVisibleColumnsChange = (newVisibleColumnIds: Set<string>): void => {
        onPageStateChange({
            ...pageState,
            visibleColumnIds: newVisibleColumnIds,
        });
    };

    return { filter, visibleColumnIds, onFilterChange, onVisibleColumnsChange };
}

export function getVersionForPageState(key: PageType): number {
    return CONSTANTS.PAGE_STATE_VERSIONS[key];
}
